import {
  IntegrationsService,
  OrgProfileIntegration,
  SftpDownloadNodeData,
  UpdateWorkflowNodeRequest,
  WorkflowsService,
} from '@/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useStore } from '@nanostores/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNodes } from '@xyflow/react';
import { Info, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { FormattedNode } from '../../WorkflowBuilder';

export default function SFTPDownloadConfiguration({
  setNodes,
  selectedNode,
  readOnly = false,
}: {
  setNodes: any;
  selectedNode: FormattedNode;
  readOnly?: boolean;
}) {
  const nodes = useNodes();
  const wF = useStore($workflow);
  const org = useStore($org);
  const queryClient = useQueryClient();

  const [fromDir, setFromDir] = useState('');
  const [remoteFileNames, setRemoteFileNames] = useState<string[]>(['']);
  const [sftpSource, setSftpSource] = useState('');

  const [
    typeOfSearchForFilesToBeDownloaded,
    setTypeOfSearchForFilesToBeDownloaded,
  ] = useState('REGEX');

  function updateSFTPConfig() {
    if (readOnly) return;
    // get the current selected node
    const newNodes = JSON.parse(JSON.stringify(nodes));

    const updatableNode = newNodes.find((n: any) => n.id === selectedNode.id);

    // find the index of selected node
    const selectedNodeIndex = newNodes.findIndex(
      (n: any) => n.id === selectedNode.id,
    );

    if (updatableNode) {
      // update the node data
      if (typeOfSearchForFilesToBeDownloaded === 'ALL_FILES_IN_DIRECTORY') {
        if (sftpSource && fromDir) {
          updatableNode.data = {
            ...updatableNode.data,
            sftpSource,
            fromDirPath: fromDir,
            typeOfSearchForFilesToBeDownloaded,
            fileNamesToBeDownloaded: null,
          };
          newNodes[selectedNodeIndex] = updatableNode;
          setNodes(newNodes);
          return updatableNode;
        }
        return toast.error('All the configuration parameters are required!', {
          duration: 5000,
        });
      } else if (typeOfSearchForFilesToBeDownloaded === 'DIRECT_FILE_NAMES') {
        if (sftpSource && fromDir && remoteFileNames.length > 0) {
          updatableNode.data = {
            ...updatableNode.data,
            sftpSource,
            fileNamesToBeDownloaded: remoteFileNames,
            fromDirPath: fromDir,
            typeOfSearchForFilesToBeDownloaded,
          };
          newNodes[selectedNodeIndex] = updatableNode;
          setNodes(newNodes);
          return updatableNode;
        }
        return toast.error('All the configuration parameters are required!', {
          duration: 5000,
        });
      } else {
        if (sftpSource && fromDir && remoteFileNames.length > 0) {
          updatableNode.data = {
            ...updatableNode.data,
            sftpSource,
            fileNamesToBeDownloaded: remoteFileNames,
            fromDirPath: fromDir,
            typeOfSearchForFilesToBeDownloaded,
          };
          newNodes[selectedNodeIndex] = updatableNode;
          setNodes(newNodes);
          return updatableNode;
        }
        return toast.error('All the configuration parameters are required!', {
          duration: 5000,
        });
      }
    }
  }

  const sftpSources = useQuery({
    queryKey: ['getOrgIntegrations', org?.id],
    queryFn: async () => {
      if (!org) return;
      const res = await IntegrationsService.getOrgIntegrations(org?.id, 'SFTP');
      console.log('res', res);
      return res;
    },
  });

  // mutation to update a node
  const updateNode = useMutation({
    mutationFn: async (payload: {
      req: UpdateWorkflowNodeRequest;
      nodeId: string;
    }) => {
      // call the api to create a new workspace
      if (wF) {
        const res = await WorkflowsService.updateWorkflowNode(
          wF.id,
          payload.nodeId,
          payload.req,
        );
        return res;
      }
    },
    onSuccess: async data => {
      await queryClient.invalidateQueries({
        queryKey: ['getWorkflow', wF?.id],
      });
      toast.success('Saved successfully!', {
        description: 'Your changes have been saved.',
        duration: 5000,
      });
    },
    onError: error => {
      console.error(error);

      // show a toast message
      toast.error('An error occurred :: Unable to add node.', {
        description: error.message,
        duration: 5000,
      });
    },
  });

  function isSftpDownloadNodeData(data: any): data is SftpDownloadNodeData {
    return data.type === 'SFTP_DOWNLOAD';
  }

  useEffect(() => {
    if (selectedNode) {
      if (isSftpDownloadNodeData(selectedNode.data)) {
        setSftpSource(selectedNode?.data?.sftpSource);
        setFromDir(selectedNode?.data?.fromDirPath);
        setTypeOfSearchForFilesToBeDownloaded(
          selectedNode?.data?.typeOfSearchForFilesToBeDownloaded || 'REGEX',
        );
        if (
          selectedNode?.data?.fileNamesToBeDownloaded &&
          Array.isArray(selectedNode?.data?.fileNamesToBeDownloaded) &&
          selectedNode?.data?.fileNamesToBeDownloaded?.length > 0
        ) {
          setRemoteFileNames(selectedNode?.data?.fileNamesToBeDownloaded);
        }
      }
    }
  }, [selectedNode]);

  return (
    <div className="h-full w-[100%] overflow-y-auto px-1">
      <div className="mt-4 grid w-full max-w-sm items-center gap-2.5">
        <div className="flex items-center">
          <Label htmlFor="email">SFTP source </Label>

          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="ml-1 h-3 w-3 text-muted-foreground hover:cursor-pointer" />
              </TooltipTrigger>
              <TooltipContent>
                <p className="w-[300px] text-xs">
                  Indicates the SFTP server from where data will be downloaded.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <Select
          onValueChange={value => {
            if (!readOnly) { // ✅ Prevent updates
              setSftpSource(value);
            }
          }}
          value={sftpSource}
          disabled={readOnly} // ✅ Disable dropdown in read-only mode
        >
          <SelectTrigger className={`w-[100%] ${readOnly ? "bg-gray-200 text-gray-500 cursor-not-allowed" : ""}`}>
            <SelectValue placeholder="Select a SFTP source" />
          </SelectTrigger>
          <SelectContent className="w-[100%]">
            <SelectGroup>
              <SelectLabel>SFTP Sources</SelectLabel>
              {sftpSources?.data?.map(
                (source: OrgProfileIntegration, s: number) => (
                  <SelectItem value={source.id} key={s}>
                    {source.name}
                  </SelectItem>
                ),
              )}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>

      <div className="mt-4 grid w-full max-w-sm items-center gap-2.5">
        <div className="flex items-center">
          <Label htmlFor="email">Remote directory path </Label>

          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="ml-1 h-3 w-3 text-muted-foreground hover:cursor-pointer" />
              </TooltipTrigger>
              <TooltipContent>
                <p className="w-[300px] text-xs">
                  This field specifies the directory on the SFTP server from
                  which files will be downloaded. Please ensure the correct
                  directory path is provided.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        <Input
          type="text"
          id="email"
          placeholder="/home/abc/project1"
          className={`italic ${readOnly ? "bg-gray-200 text-gray-500 cursor-not-allowed" : ""}`}
          value={fromDir}
          disabled={readOnly} // ✅ Disable input in read-only mode
          onChange={e => {
            if (!readOnly) { // ✅ Prevent updates
              setFromDir(e.target.value);
            }
          }}
        />

      </div>

      <div className="mt-4 grid w-full max-w-sm items-center gap-2.5">
        <div className="flex items-center">
          <Label>Files to download</Label>
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="ml-1 h-3 w-3 text-muted-foreground hover:cursor-pointer" />
              </TooltipTrigger>
              <TooltipContent>
                <p className="w-[300px] text-xs">
                  This field specifies the criteria for how Rally should search
                  for and download files. Please select the desired search
                  pattern.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <RadioGroup
          defaultValue="DIRECT_FILE_NAMES"
          value={typeOfSearchForFilesToBeDownloaded}
          disabled={readOnly} // ✅ Disable radio group in read-only mode
          onValueChange={(value: string) => {
            if (!readOnly) { // ✅ Prevent updates
              if (value === 'DIRECT_FILE_NAMES') {
                setRemoteFileNames(['']);
              } else {
                setRemoteFileNames(['', '', '']);
              }
              setTypeOfSearchForFilesToBeDownloaded(value);
            }
          }}
        >

          <div className="flex items-center space-x-2">
            <RadioGroupItem value="REGEX" id="r1" />
            <Label htmlFor="r1">
              Use Regex to search files to be downloaded
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="DIRECT_FILE_NAMES" id="r2" />
            <Label htmlFor="r2">
              Use direct file names to search files to be downloaded
            </Label>
          </div>

          <div className="flex items-center space-x-2">
            <RadioGroupItem value="ALL_FILES_IN_DIRECTORY" id="r2" />
            <Label htmlFor="r2">
              Download all the files in the specified directory
            </Label>
          </div>
        </RadioGroup>
      </div>

      {typeOfSearchForFilesToBeDownloaded !== 'ALL_FILES_IN_DIRECTORY' && (
        <div className="mt-4 grid w-full max-w-sm items-center gap-2.5">
          <Label>Remote file names</Label>
          {remoteFileNames?.map((fileName, i) => (
            <div className="flex items-center">
              <Input
                type="text"
                id="email"
                placeholder="call_records_01.csv"
                className={`italic ${readOnly ? "bg-gray-200 text-gray-500 cursor-not-allowed" : ""}`}
                value={fileName}
                disabled={readOnly} // ✅ Disable input in read-only mode
                onChange={e => {
                  if (!readOnly) { // ✅ Prevent updates
                    const updatedFileNames: string[] = [...remoteFileNames];
                    updatedFileNames[i] = e.target.value;
                    setRemoteFileNames(updatedFileNames);
                  }
                }}
                key={i}
              />

              {typeOfSearchForFilesToBeDownloaded === 'DIRECT_FILE_NAMES' && (
                <>
                  {remoteFileNames?.length > 1 && (
                    <Button
                      variant="outline"
                      size="icon"
                      className="ml-1"
                      onClick={() => {
                        if (!readOnly) {
                          const updatedFileNames: string[] = [...remoteFileNames];
                          updatedFileNames.splice(i, 1);
                          setRemoteFileNames(updatedFileNames);
                        }
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}

                  <Button
                    variant="outline"
                    size="icon"
                    className="ml-1"
                    onClick={() => {
                      if (!readOnly) {
                        setRemoteFileNames([...remoteFileNames, '']);
                      }
                    }}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </>
              )}
            </div>
          ))}

          <div className="mt-0 flex w-full max-w-sm gap-1.5 rounded-md bg-slate-200 px-2 py-2 text-xs">
            <div className="flex">
              <Info className="mr-1 mt-0.5 h-3 w-3 text-muted-foreground" />
            </div>
            <div className="flex">
              {typeOfSearchForFilesToBeDownloaded === 'REGEX' && (
                <p>
                  Rally will use regular expressions to search for similar files
                  on the sepcified remote directory.
                </p>
              )}

              {typeOfSearchForFilesToBeDownloaded === 'DIRECT_FILE_NAMES' && (
                <p>
                  Rally will only search for the specified files on the the
                  remote directory.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* <div className="mt-4 grid w-full max-w-sm items-center gap-1.5">
        <Label>Remote file names</Label>
        <Input
          type="text"
          id="email"
          placeholder="call_records_01.csv"
          className="italic"
          value={remoteFileName0}
          onChange={e => setRemoteFileName0(e.target.value)}
        />
        <Input
          type="text"
          id="email"
          placeholder="call_records_02.csv"
          className="italic"
          value={remoteFileName1}
          onChange={e => setRemoteFileName1(e.target.value)}
        />
        <Input
          type="text"
          id="email"
          placeholder="call_records_03.csv"
          className="italic"
          value={remoteFileName2}
          onChange={e => setRemoteFileName2(e.target.value)}
        />

        <div className="mt-0 flex w-full max-w-sm gap-1.5 rounded-md bg-slate-200 px-2 py-2 text-xs">
          <div className="flex">
            <Info className="mr-1 mt-0.5 h-3 w-3 text-muted-foreground" />
          </div>
          <div className="flex">
            Please enter three filenames that have a common part and a dynamic
            part. The common part is something that stays the same across all
            filenames (e.g., a prefix or suffix), while the dynamic part changes
            (e.g., a number or date). For example, you could enter filenames
            like report_20240101.txt, report_20240201.csv, and
            report_20240301.pdf. Rally will use these filenames to create a
            pattern that helps download all matching files from your SFTP
            server.
          </div>
        </div>
      </div>

      {remoteDir && remoteFileName0 && (
        <div className="mt-2 flex w-full max-w-sm  gap-1.5 rounded-md bg-slate-200 px-2 py-2 text-xs">
          <div className="flex">
            <Info className="mr-1 mt-0.5 h-3 w-3 text-muted-foreground" />
          </div>
          <div className="flex flex-col">
            Rally will search similar files on the directory :
            <code className="mt-2">
              <blockquote className="italic">{remoteDir}</blockquote>
            </code>
          </div>
        </div>
      )} */}

      <div className="mt-4 flex justify-end">
        <Button
          variant="outline"
          size="sm"
          disabled={readOnly || updateNode.isPending}
          className={readOnly ? "bg-gray-200 text-gray-500 cursor-not-allowed" : ""}
          onClick={async () => {
            if (!readOnly) {
              const updatableNode = updateSFTPConfig();
              if (updatableNode) {
                await updateNode.mutateAsync({
                  req: { data: { ...updatableNode.data, type: 'SFTP_DOWNLOAD' } },
                  nodeId: updatableNode?.id,
                });
              }
            }
          }}
        >
          Save
        </Button>
      </div>
    </div>
  );
}
